import * as React from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useSpring, animated, useTransition, config } from '@react-spring/web';

import Popover from '@mui/material/Popover';
import MDBox from 'components/MDBox';
import Icon from '@mui/material/Icon';
import MDButton from 'components/MDButton';
import { getStocks } from 'services/outside/aaro/stocks';
import TimelineList from 'examples/Timeline/TimelineList';
import TimelineItem from './TimelineItem';

const fetchAllStockAmountsForSingleStock = async (StokID) => {
    const response = await getStocks({
        StokID,
    });
    return response;
};

export default function MouseOverPopover({ stockID }) {
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [shouldFetch, setShouldFetch] = React.useState(false);

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setShouldFetch(true);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <MDBox
            onMouseEnter={async () => {
                // Prefetch the data when mouse enters
                await queryClient.prefetchQuery({
                    queryKey: ['fetchStockAmountsForStock', stockID],
                    queryFn: () => fetchAllStockAmountsForSingleStock(stockID),
                    staleTime: 60 * 1000,
                });
            }}
            display="flex"
            justifyContent="center"
            alignItems="center"
        >
            <MDButton
                size="medium"
                variant="gradient"
                color="warning"
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                iconOnly
            >
                <Icon>home</Icon>
            </MDButton>

            <Popover
                id="mouse-over-popover"
                sx={{
                    pointerEvents: 'none',
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
            >
                <StockView stockID={stockID} shouldFetch={shouldFetch} />
            </Popover>
        </MDBox>
    );
}

const StockView = ({ stockID, shouldFetch }) => {
    // Use useQuery to reactively get data from cache or fetch if needed
    const { data, isLoading } = useQuery({
        queryKey: ['fetchStockAmountsForStock', stockID],
        queryFn: () => fetchAllStockAmountsForSingleStock(stockID),
        enabled: shouldFetch, // Only enable when we actually need the data
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const stockDetails = data || [];
    const stockName = stockDetails[0] ? stockDetails[0].StokAdi : 'Aranıyor...';

    // Loading animation - pulsing effect
    const loadingAnimation = useSpring({
        opacity: isLoading ? 1 : 0,
        scale: isLoading ? 1 : 0,
        config: config.wobbly,
    });

    // Pulsing animation for loading text
    const pulseAnimation = useSpring({
        from: { opacity: 0.5, scale: 0.95 },
        to: async (next) => {
            while (isLoading) {
                await next({ opacity: 1, scale: 1 });
                await next({ opacity: 0.5, scale: 0.95 });
            }
        },
        config: config.slow,
    });

    // Content animation - fade in when data is ready
    const contentAnimation = useSpring({
        opacity: !isLoading && data ? 1 : 0,
        transform: !isLoading && data ? 'translateY(0px)' : 'translateY(10px)',
        config: config.gentle,
    });

    // Timeline items animation with stagger effect
    const timelineTransitions = useTransition(!isLoading && data ? stockDetails : [], {
        from: { opacity: 0, transform: 'translateX(-20px) scale(0.9)' },
        enter: { opacity: 1, transform: 'translateX(0px) scale(1)' },
        leave: { opacity: 0, transform: 'translateX(20px) scale(0.9)' },
        config: config.gentle,
        trail: 80, // Stagger delay between timeline items
        keys: (item) => item.StokID + '-' + item.DepoID,
    });

    if (isLoading || !data) {
        return (
            <animated.div style={loadingAnimation}>
                <TimelineList title="Stok Bilgileri" dark>
                    <animated.div style={pulseAnimation}>
                        <MDBox p={2} textAlign="center">
                            <Icon sx={{ fontSize: 40, opacity: 0.6 }}>inventory</Icon>
                            <MDBox mt={1}>
                                <span style={{ color: 'white' }}>Stok bilgileri aranıyor...</span>
                            </MDBox>
                        </MDBox>
                    </animated.div>
                </TimelineList>
            </animated.div>
        );
    }

    return (
        <animated.div style={contentAnimation}>
            <TimelineList title={stockName} dark>
                {timelineTransitions((style, el) => (
                    <animated.div key={el.StokID + '-' + el.DepoID} style={style}>
                        <TimelineItem
                            color={el.Miktar > 5 ? 'success' : el.Miktar > 0 ? 'warning' : 'primary'}
                            icon={el.Miktar > 5 ? 'check_circle' : el.Miktar > 0 ? 'question_mark' : 'remove_circle'}
                            title={el.DepoAdi}
                            dateTime={el.Miktar + ' - ' + el.Brm1Adi}
                        />
                    </animated.div>
                ))}
            </TimelineList>
        </animated.div>
    );
};
